/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import {
    Box, Dialog, DialogContent, Typography, Stack, Button,
    IconButton, CircularProgress, Divider, TextField, alpha, Tooltip, Collapse,
} from '@mui/material';
import { AnimatePresence, m } from 'framer-motion';
import Iconify from 'src/components/Iconify';
import { useSelector, dispatch } from '../../../redux/store';
import {
    createLeadTreatmentPlan,
    updateLeadTreatmentPlan,
    sendLeadTreatmentPlan,
    getTreatmentPlanTemplates,
} from '../../../redux/slices/consentV2';
import { getPracticeTreatmentList } from '../../../redux/slices/practiceTreatment';
import useSnackbar from '../../../hooks/useCustomSnackbar';
import TreatmentPlanOptionTabs from './TreatmentPlanOptionTabs';
import TreatmentPlanStartSelector from './TreatmentPlanStartSelector';
import TreatmentPlanTimeline from './TreatmentPlanTimeline';
import TreatmentPlanVisualsNotes from './TreatmentPlanVisualsNotes';
import TreatmentPlanPreview from './TreatmentPlanPreview';

// ─── Channel config ───────────────────────────────────────────────────────────
const CHANNELS = [
    { value: 'whatsapp', label: 'WhatsApp', icon: 'logos:whatsapp-icon' },
    { value: 'email', label: 'Email', icon: 'eva:email-fill' },
    { value: 'sms', label: 'SMS', icon: 'eva:message-square-fill' },
];

// ─── Wizard steps (3-step flow) ────────────────────────────────────────────────
const WIZARD_STEPS = [
    { id: 'treatments', label: 'Treatments', icon: 'solar:tooth-bold-duotone' },
    { id: 'details', label: 'Build Plan', icon: 'solar:document-text-bold-duotone' },
    { id: 'preview', label: 'Preview & Send', icon: 'eva:paper-plane-fill' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function createBlankOption(name = 'Treatment Package') {
    return {
        id: uuidv4(),
        name,
        treatmentName: '',
        description: '',
        reason: '',
        alternatives: '',
        duration: '',
        appointments: '',
        plainEnglish: '',
        costItems: [],
        deposit: '',
        paymentPlan: { type: 'no', details: '' },
        included: '',
        excluded: '',
        phases: [],
    };
}

function buildOptionsFromTreatments(treatments, matchedTemplates) {
    const option = createBlankOption('Treatment Package');

    // All treatments become cost line items in this single package
    option.costItems = treatments.map((t) => ({
        id: uuidv4(),
        description: t.name || 'Treatment',
        qty: '1',
        unitPrice: String(t.price || ''),
        lineTotal: String(t.price || ''),
    }));

    // Build treatment name from all selected treatments
    const treatmentNames = treatments.map(t => t.name).filter(Boolean);
    option.treatmentName = treatmentNames.join(', ');

    // If a single matched template exists, use its pricing line items instead
    const tpl = matchedTemplates && matchedTemplates.length === 1 ? matchedTemplates[0] : null;
    if (tpl?.pricing_template?.line_items?.length > 0) {
        option.costItems = tpl.pricing_template.line_items.map((item) => ({
            id: uuidv4(),
            description: item.item_name || '',
            qty: String(item.quantity || 1),
            unitPrice: String(item.unit_cost || ''),
            lineTotal: String((parseFloat(item.unit_cost) || 0) * (parseInt(item.quantity, 10) || 1)),
        }));
    }

    // Apply template content to option fields
    if (tpl?.content) {
        option.included = tpl.content.included || '';
        option.excluded = tpl.content.excluded || '';
        option.description = tpl.content.description || '';
        option.duration = tpl.content.duration || '';
        option.appointments = tpl.content.appointments || '';
    }

    // Apply template payment config
    if (tpl?.payment_config) {
        const cfg = tpl.payment_config;
        const total = option.costItems.reduce((s, i) => s + ((parseFloat(i.qty) || 0) * (parseFloat(i.unitPrice) || 0)), 0);
        if (cfg.deposit_percentage && total > 0) {
            option.deposit = String(Math.round(total * (parseFloat(cfg.deposit_percentage) / 100)));
        }
        if (cfg.installment_count && parseInt(cfg.installment_count, 10) > 0) {
            option.paymentPlan = { type: 'monthly', details: `${cfg.installment_count} monthly instalments` };
        }
    }

    const planName = treatmentNames.length === 1
        ? `${treatmentNames[0]} Treatment Plan`
        : 'Treatment Plan';

    // Return matched template for plan-level fields
    return { options: [option], planName, matchedTemplate: tpl };
}

function buildOptionsFromLeadTreatments(selectedTreatments, matchedTemplates) {
    const option = createBlankOption('Treatment Package');

    option.costItems = selectedTreatments.map((treatment) => {
        const treatmentName = treatment?.treatment_item?.name || '';
        const totalPrice = treatment?.custom_price || treatment?.treatment_item?.price || '';
        return {
            id: uuidv4(),
            description: treatmentName || 'Treatment',
            qty: '1',
            unitPrice: String(totalPrice),
            lineTotal: String(totalPrice),
        };
    });

    const treatmentNames = selectedTreatments.map(t => t?.treatment_item?.name).filter(Boolean);
    option.treatmentName = treatmentNames.join(', ');

    // If a single matched template exists, apply its data
    const tpl = matchedTemplates && matchedTemplates.length === 1 ? matchedTemplates[0] : null;
    if (tpl?.pricing_template?.line_items?.length > 0) {
        option.costItems = tpl.pricing_template.line_items.map((item) => ({
            id: uuidv4(),
            description: item.item_name || '',
            qty: String(item.quantity || 1),
            unitPrice: String(item.unit_cost || ''),
            lineTotal: String((parseFloat(item.unit_cost) || 0) * (parseInt(item.quantity, 10) || 1)),
        }));
    }
    if (tpl?.content) {
        option.included = tpl.content.included || '';
        option.excluded = tpl.content.excluded || '';
        option.description = tpl.content.description || '';
        option.duration = tpl.content.duration || '';
        option.appointments = tpl.content.appointments || '';
    }
    if (tpl?.payment_config) {
        const cfg = tpl.payment_config;
        const total = option.costItems.reduce((s, i) => s + ((parseFloat(i.qty) || 0) * (parseFloat(i.unitPrice) || 0)), 0);
        if (cfg.deposit_percentage && total > 0) {
            option.deposit = String(Math.round(total * (parseFloat(cfg.deposit_percentage) / 100)));
        }
        if (cfg.installment_count && parseInt(cfg.installment_count, 10) > 0) {
            option.paymentPlan = { type: 'monthly', details: `${cfg.installment_count} monthly instalments` };
        }
    }

    const planName = treatmentNames.length === 1
        ? `${treatmentNames[0]} Treatment Plan`
        : 'Treatment Plan';

    return { options: [option], planName, matchedTemplate: tpl };
}

// ─── WizardStepper ────────────────────────────────────────────────────────────

function WizardStepper({ currentStep, totalSteps, onStepClick }) {
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
            {WIZARD_STEPS.slice(0, totalSteps).map((step, idx) => {
                const isCompleted = idx < currentStep;
                const isCurrent = idx === currentStep;
                const isClickable = isCompleted;

                return (
                    <Stack
                        key={step.id}
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        onClick={() => isClickable && onStepClick(idx)}
                        sx={{
                            cursor: isClickable ? 'pointer' : 'default',
                            opacity: isCurrent ? 1 : (isCompleted ? 0.85 : 0.4),
                            '&:hover': isClickable ? { opacity: 1 } : {},
                            transition: 'opacity 0.2s',
                        }}
                    >
                        <Box
                            sx={{
                                width: 22, height: 22, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                bgcolor: isCurrent ? '#00AB55' : (isCompleted ? alpha('#00AB55', 0.15) : 'rgba(145,158,171,0.12)'),
                                transition: 'all 0.25s ease',
                            }}
                        >
                            {isCompleted ? (
                                <Iconify icon="eva:checkmark-fill" width={12} sx={{ color: '#00AB55' }} />
                            ) : (
                                <Typography sx={{ fontSize: '10px', fontWeight: 700, color: isCurrent ? '#fff' : '#919EAB' }}>
                                    {idx + 1}
                                </Typography>
                            )}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontSize: '11px',
                                fontWeight: isCurrent ? 700 : 500,
                                color: isCurrent ? '#212B36' : '#919EAB',
                                display: { xs: 'none', sm: 'block' },
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {step.label}
                        </Typography>
                        {idx < totalSteps - 1 && (
                            <Box
                                sx={{
                                    width: 16, height: 1,
                                    bgcolor: isCompleted ? alpha('#00AB55', 0.3) : 'rgba(145,158,171,0.2)',
                                    display: { xs: 'none', sm: 'block' },
                                }}
                            />
                        )}
                    </Stack>
                );
            })}
        </Stack>
    );
}

WizardStepper.propTypes = {
    currentStep: PropTypes.number.isRequired,
    totalSteps: PropTypes.number.isRequired,
    onStepClick: PropTypes.func.isRequired,
};

// ─── LinkScreen ───────────────────────────────────────────────────────────────

function LinkScreen({ plan, onClose, onSend, leadDetail }) {
    const { enqueueSnackbar } = useSnackbar();
    const [sending, setSending] = useState(null);
    const link = plan?.link || plan?.form_link || '';

    const handleCopy = () => {
        if (!link) return;
        navigator.clipboard.writeText(link)
            .then(() => enqueueSnackbar('Link copied', { variant: 'success' }))
            .catch(() => enqueueSnackbar('Could not copy link', { variant: 'error' }));
    };

    const handleSend = (channel) => {
        setSending(channel);
        onSend(channel, () => setSending(null));
    };

    return (
        <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: 'rgba(0,171,85,0.1)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mx: 'auto', mb: 2,
            }}>
                <Iconify icon="eva:checkmark-circle-2-fill" width={32} sx={{ color: '#00AB55' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Treatment plan created
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, fontSize: '13px' }}>
                Share this link with {leadDetail?.first_name || 'the patient'} to review and accept
            </Typography>

            {link && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: '8px 12px', borderRadius: '10px',
                    bgcolor: '#F4F6F8', border: '1px solid rgba(145,158,171,0.16)',
                    mb: 3, textAlign: 'left',
                }}>
                    <Typography variant="caption" sx={{ flex: 1, fontSize: '12px', color: '#637381', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {link}
                    </Typography>
                    <Tooltip title="Copy link">
                        <IconButton size="small" onClick={handleCopy} sx={{ flexShrink: 0 }}>
                            <Iconify icon="eva:copy-fill" width={15} sx={{ color: '#637381' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 1.5 }}>
                Send via
            </Typography>
            <Stack direction="row" justifyContent="center" spacing={1.5} sx={{ mb: 3 }}>
                {CHANNELS.map((ch) => (
                    <Button
                        key={ch.value}
                        variant="outlined"
                        size="small"
                        startIcon={sending === ch.value ? <CircularProgress size={13} /> : <Iconify icon={ch.icon} width={16} />}
                        onClick={() => handleSend(ch.value)}
                        disabled={!!sending}
                        sx={{
                            textTransform: 'none', fontWeight: 600, fontSize: '12px',
                            borderRadius: '8px', borderColor: 'rgba(145,158,171,0.3)', color: '#637381',
                            '&:hover': { borderColor: '#00AB55', color: '#00AB55', bgcolor: alpha('#00AB55', 0.04) },
                        }}
                    >
                        {ch.label}
                    </Button>
                ))}
            </Stack>

            <Button
                variant="contained"
                onClick={onClose}
                sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, bgcolor: '#00AB55', '&:hover': { bgcolor: '#007B40' } }}
            >
                Done
            </Button>
        </Box>
    );
}

LinkScreen.propTypes = {
    plan: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    onSend: PropTypes.func.isRequired,
    leadDetail: PropTypes.object,
};
LinkScreen.defaultProps = { plan: null, leadDetail: null };

// ─── Main dialog ──────────────────────────────────────────────────────────────

export default function TreatmentPlanCurationDialog({
    open,
    onClose,
    leadDetail,
    existingPlan,
}) {
    const { enqueueSnackbar } = useSnackbar();
    const practiceId = useSelector((state) => state.practiceTreatmentList.practiceId);
    const { treatmentPlanTemplates } = useSelector((state) => state.consentV2);
    const practiceTreatmentsFull = useSelector((state) => state.practiceTreatment?.practiceTreatment?.results) || [];
    const practiceTreatmentsList = useSelector((state) => state.practiceTreatment?.treatmentsList?.results) || [];
    // Use whichever list has data — full list from settings, or paginated list from dropdowns
    const practiceTreatments = practiceTreatmentsFull.length > 0 ? practiceTreatmentsFull : practiceTreatmentsList;
    const leadId = leadDetail?.id;

    // Wizard step (0-2 for wizard, 'link' for final send screen)
    const [wizardStep, setWizardStep] = useState(0);

    // Form state
    const [planName, setPlanName] = useState('');
    const [planFields, setPlanFields] = useState({ reason: '', alternatives: '', plainEnglish: '' });
    const [options, setOptions] = useState([]);
    const [autoFilledFields, setAutoFilledFields] = useState([]);
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [patientNotes, setPatientNotes] = useState('');
    const [phases, setPhases] = useState([]);

    // Collapsible sections state (Step 1)
    const [expandedSections, setExpandedSections] = useState({
        timeline: false,
        visuals: false,
    });

    // Dialog state
    const [saving, setSaving] = useState(false);
    const [savedPlan, setSavedPlan] = useState(null);
    const [showLinkScreen, setShowLinkScreen] = useState(false);

    const isEditing = !!existingPlan;
    const TOTAL_STEPS = 3;

    // ─── Init ──────────────────────────────────────────────────────────────────
    // ─── Load templates and practice treatments when dialog opens ─────────────
    useEffect(() => {
        if (open && practiceId) {
            dispatch(getTreatmentPlanTemplates(practiceId, { limit: 100 }));
            // Fetch practice treatments for the cost item autocomplete
            if (practiceTreatments.length === 0) {
                dispatch(getPracticeTreatmentList(practiceId, { limit: 200, offset: 0 }));
            }
        }
    }, [open, practiceId]);

    useEffect(() => {
        if (!open) return;

        setSavedPlan(null);
        setPlanFields({ reason: '', alternatives: '', plainEnglish: '' });
        setThumbnailUrl('');
        setPatientNotes('');
        setPhases([]);
        setShowLinkScreen(false);
        setExpandedSections({ timeline: false, visuals: false });

        if (existingPlan) {
            // Editing — skip treatment picker, start at details
            setPlanName(existingPlan.name || '');
            setOptions(existingPlan.options || [createBlankOption('Treatment Package')]);
            setPlanFields(existingPlan.plan_fields || { reason: '', alternatives: '', plainEnglish: '' });
            setThumbnailUrl(existingPlan.thumbnail_url || existingPlan.plan_fields?.thumbnailUrl || '');
            setPatientNotes(existingPlan.patient_notes || existingPlan.plan_fields?.patientNotes || '');
            setPhases(existingPlan.phases || existingPlan.plan_fields?.phases || []);
            setAutoFilledFields([]);
            setWizardStep(1); // Start at build plan step
        } else {
            setOptions([]);
            setAutoFilledFields([]);
            setWizardStep(0); // Start at treatment picker
        }
    }, [open, existingPlan]);

    // ─── Find matching templates for selected treatments ────────────────────
    const findMatchedTemplates = useCallback((treatmentIds) => {
        const templates = treatmentPlanTemplates?.results || [];
        if (!templates.length || !treatmentIds.length) return [];
        return templates.filter(
            (tpl) => tpl.linked_treatment_id && treatmentIds.includes(tpl.linked_treatment_id)
        );
    }, [treatmentPlanTemplates]);

    // Apply matched template's plan-level fields
    const applyTemplateToState = useCallback((matchedTemplate) => {
        if (!matchedTemplate) return;
        const filled = ['treatmentName', 'costItems'];
        if (matchedTemplate.content) {
            const c = matchedTemplate.content;
            setPlanFields({
                reason: c.reason || c.reasonForTreatment || '',
                alternatives: c.alternatives || '',
                plainEnglish: c.plainEnglish || '',
            });
            filled.push('reason', 'alternatives', 'plainEnglish');
        }
        if (matchedTemplate.phases?.length > 0) {
            setPhases(matchedTemplate.phases);
            filled.push('phases');
        }
        setAutoFilledFields(filled);
    }, []);

    // ─── Treatment picker handler (Step 0 → Step 1) ──────────────────────────
    const handleTreatmentSelect = useCallback(({ path, treatments, leadTreatments: selectedLeadTreatments }) => {
        if (path === 'scratch') {
            setOptions([createBlankOption('Treatment Package')]);
            setPlanName('Treatment Plan');
            setAutoFilledFields([]);
        } else if (path === 'lead' && selectedLeadTreatments && selectedLeadTreatments.length > 0) {
            // Match templates by the practice treatment ID behind each lead treatment
            const practiceIds = selectedLeadTreatments
                .map((lt) => lt.treatment_item?.id || lt.treatment)
                .filter(Boolean);
            const matched = findMatchedTemplates(practiceIds);
            const built = buildOptionsFromLeadTreatments(selectedLeadTreatments, matched);
            setOptions(built.options);
            setPlanName(built.planName);
            applyTemplateToState(built.matchedTemplate);
            if (!built.matchedTemplate) setAutoFilledFields(['treatmentName', 'costItems']);
        } else if (treatments && treatments.length > 0) {
            // Match templates by practiceId on each selected treatment
            const practiceIds = treatments.map((t) => t.practiceId).filter(Boolean);
            const matched = findMatchedTemplates(practiceIds);
            const built = buildOptionsFromTreatments(treatments, matched);
            setOptions(built.options);
            setPlanName(built.planName);
            applyTemplateToState(built.matchedTemplate);
            if (!built.matchedTemplate) setAutoFilledFields(['treatmentName', 'costItems']);
        } else {
            setOptions([createBlankOption('Treatment Package')]);
            setPlanName('Treatment Plan');
            setAutoFilledFields([]);
        }

        setWizardStep(1);
    }, [findMatchedTemplates, applyTemplateToState]);

    // ─── Navigation ──────────────────────────────────────────────────────────
    const handleNext = useCallback(() => {
        if (wizardStep < TOTAL_STEPS - 1) {
            setWizardStep((prev) => prev + 1);
        }
    }, [wizardStep]);

    const handleBack = useCallback(() => {
        if (wizardStep > 0) {
            setWizardStep((prev) => prev - 1);
        }
    }, [wizardStep]);

    const handleStepClick = useCallback((idx) => {
        if (idx < wizardStep) setWizardStep(idx);
    }, [wizardStep]);

    // ─── Save & generate ──────────────────────────────────────────────────────
    const handleGenerate = useCallback(() => {
        if (!planName.trim()) {
            enqueueSnackbar('Please enter a plan name', { variant: 'warning' });
            setWizardStep(1);
            return;
        }

        const payload = {
            name: planName.trim(),
            options: options.map((opt) => ({ ...opt, costItems: opt.costItems || [] })),
            plan_fields: {
                ...planFields,
                thumbnailUrl: thumbnailUrl || undefined,
                patientNotes: patientNotes || undefined,
                phases: phases.length > 0 ? phases : undefined,
            },
            form_id: null,
        };

        setSaving(true);

        if (isEditing && existingPlan?.id) {
            dispatch(updateLeadTreatmentPlan(practiceId, leadId, existingPlan.id, payload, ({ success, data }) => {
                setSaving(false);
                if (success) { setSavedPlan(data); setShowLinkScreen(true); }
                else enqueueSnackbar('Failed to update treatment plan', { variant: 'error' });
            }));
        } else {
            dispatch(createLeadTreatmentPlan(practiceId, leadId, payload, ({ success, data }) => {
                setSaving(false);
                if (success) { setSavedPlan(data); setShowLinkScreen(true); }
                else enqueueSnackbar('Failed to create treatment plan', { variant: 'error' });
            }));
        }
    }, [planName, options, planFields, thumbnailUrl, patientNotes, phases, isEditing, existingPlan, practiceId, leadId, enqueueSnackbar]);

    const handleSend = useCallback((channel, callback) => {
        const planId = savedPlan?.id || existingPlan?.id;
        if (!planId) { callback?.(); return; }
        dispatch(sendLeadTreatmentPlan(practiceId, leadId, planId, channel, ({ success }) => {
            callback?.();
            if (success) enqueueSnackbar(`Treatment plan sent via ${channel}`, { variant: 'success' });
            else enqueueSnackbar('Failed to send — try again', { variant: 'error' });
        }));
    }, [savedPlan, existingPlan, practiceId, leadId, enqueueSnackbar]);

    const handleClose = () => {
        if (saving) return;
        onClose(showLinkScreen);
    };

    const patientName = [leadDetail?.first_name, leadDetail?.last_name].filter(Boolean).join(' ') || 'Patient';

    // Step-specific validation for Next button
    const canProceed = useMemo(() => {
        if (wizardStep === 0) return false; // Step 0 has its own Continue
        if (wizardStep === 1) return planName.trim() && options.length > 0;
        return true; // Step 2 (preview) always allows generate
    }, [wizardStep, planName, options]);

    // Toggle section expansion
    const toggleSection = (section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen
            PaperProps={{ sx: { bgcolor: '#F8FAFC' } }}
        >
            {/* ── Header ── */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 3, py: 1.5,
                bgcolor: '#fff', borderBottom: '1px solid rgba(145,158,171,0.16)',
                position: 'sticky', top: 0, zIndex: 10,
                gap: 2,
            }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                    <Iconify icon="solar:document-medicine-bold-duotone" width={22} sx={{ color: '#00AB55', flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '15px', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {isEditing ? 'Edit Treatment Plan' : 'Create Treatment Plan'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
                            For: {patientName}
                        </Typography>
                    </Box>
                </Stack>

                {/* Stepper — only show when past treatment picker and not on link screen */}
                {!showLinkScreen && wizardStep > 0 && (
                    <WizardStepper
                        currentStep={wizardStep}
                        totalSteps={TOTAL_STEPS}
                        onStepClick={handleStepClick}
                    />
                )}

                <IconButton onClick={handleClose} disabled={saving} sx={{ flexShrink: 0 }}>
                    <Iconify icon="eva:close-fill" width={20} />
                </IconButton>
            </Box>

            {/* ── Body ── */}
            <DialogContent sx={{ p: 0, overflow: 'auto' }}>
                <AnimatePresence mode="wait">
                    {showLinkScreen && (
                        <m.div
                            key="link-screen"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <LinkScreen
                                plan={savedPlan}
                                onClose={handleClose}
                                onSend={handleSend}
                                leadDetail={leadDetail}
                            />
                        </m.div>
                    )}

                    {!showLinkScreen && wizardStep === 0 && (
                        <m.div
                            key="step-0"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <TreatmentPlanStartSelector
                                leadDetail={leadDetail}
                                onSelect={handleTreatmentSelect}
                            />
                        </m.div>
                    )}

                    {!showLinkScreen && wizardStep === 1 && (
                        <m.div
                            key="step-1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                                {/* Plan name */}
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Plan name
                                    </Typography>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        placeholder="e.g. Dental Implants Treatment Plan"
                                        value={planName}
                                        onChange={(e) => setPlanName(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '10px', fontSize: '15px', fontWeight: 600, bgcolor: '#fff',
                                                '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
                                                '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
                                            },
                                            '& .MuiOutlinedInput-input': { py: '10px', px: '12px' },
                                        }}
                                    />
                                </Box>

                                {/* Treatment Context */}
                                <Box
                                    sx={{
                                        bgcolor: '#fff', borderRadius: '14px',
                                        border: '1px solid rgba(145,158,171,0.16)',
                                        px: 2.5, pt: 2, pb: 2.5, mb: 3,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                                        <Iconify icon="solar:notes-bold-duotone" width={16} sx={{ color: '#637381' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            🎯 Treatment Context
                                        </Typography>
                                    </Stack>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                                        {[
                                            { key: 'reason', label: 'Why is this treatment recommended?', rows: 2, placeholder: 'Explain the clinical reasoning for this patient...' },
                                            { key: 'alternatives', label: 'Alternatives considered', rows: 2, placeholder: 'List alternatives you considered...' },
                                            { key: 'plainEnglish', label: 'Plain English summary', rows: 3, placeholder: 'Write a patient-friendly overview here...' },
                                        ].map(({ key, label, rows, placeholder }) => (
                                            <Box key={key}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {label}
                                                </Typography>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    rows={rows}
                                                    placeholder={placeholder}
                                                    value={planFields[key] || ''}
                                                    onChange={(e) => setPlanFields((prev) => ({ ...prev, [key]: e.target.value }))}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '8px', fontSize: '13px', bgcolor: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
                                                            alignItems: 'flex-start',
                                                        },
                                                        '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
                                                    }}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Package Details */}
                                <Box
                                    sx={{
                                        bgcolor: '#fff', borderRadius: '14px',
                                        border: '1px solid rgba(145,158,171,0.16)',
                                        px: 2.5, pt: 2, pb: 3, mb: 3,
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <Iconify icon="solar:list-bold-duotone" width={18} sx={{ color: '#00AB55' }} />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                                            📦 Package Details
                                        </Typography>
                                        <Box sx={{ px: '8px', py: '2px', borderRadius: '6px', bgcolor: 'rgba(0,171,85,0.08)' }}>
                                            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#00AB55' }}>
                                                {options.length} package{options.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px', display: 'block', mb: 2 }}>
                                        Edit treatment line items, pricing, and included/excluded services.
                                    </Typography>

                                    <TreatmentPlanOptionTabs
                                        options={options}
                                        onChange={setOptions}
                                        autoFilledFields={autoFilledFields}
                                        treatments={practiceTreatments}
                                    />
                                </Box>

                                {/* Timeline — Collapsed Section */}
                                <Box
                                    sx={{
                                        bgcolor: '#fff', borderRadius: '14px',
                                        border: '1px solid rgba(145,158,171,0.16)',
                                        overflow: 'hidden', mb: 3,
                                    }}
                                >
                                    <Box
                                        onClick={() => toggleSection('timeline')}
                                        sx={{
                                            px: 2.5, py: 1.5,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'rgba(0,171,85,0.04)' },
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Iconify icon="eva:calendar-fill" width={16} sx={{ color: '#637381' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                📅 Timeline
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                                                · Optional
                                            </Typography>
                                        </Stack>
                                        <Iconify
                                            icon={expandedSections.timeline ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                                            width={16}
                                            sx={{ color: '#637381', transition: 'transform 0.2s' }}
                                        />
                                    </Box>
                                    <Collapse in={expandedSections.timeline} timeout="auto" unmountOnExit>
                                        <Divider />
                                        <Box sx={{ px: 2.5, py: 2.5 }}>
                                            <TreatmentPlanTimeline
                                                phases={phases}
                                                onPhasesChange={setPhases}
                                            />
                                        </Box>
                                    </Collapse>
                                </Box>

                                {/* Visuals & Notes — Collapsed Section */}
                                <Box
                                    sx={{
                                        bgcolor: '#fff', borderRadius: '14px',
                                        border: '1px solid rgba(145,158,171,0.16)',
                                        overflow: 'hidden', mb: 3,
                                    }}
                                >
                                    <Box
                                        onClick={() => toggleSection('visuals')}
                                        sx={{
                                            px: 2.5, py: 1.5,
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'rgba(0,171,85,0.04)' },
                                            transition: 'all 0.2s',
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Iconify icon="eva:image-fill" width={16} sx={{ color: '#637381' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                🖼 Visuals & Notes
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                                                · Optional
                                            </Typography>
                                        </Stack>
                                        <Iconify
                                            icon={expandedSections.visuals ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                                            width={16}
                                            sx={{ color: '#637381', transition: 'transform 0.2s' }}
                                        />
                                    </Box>
                                    <Collapse in={expandedSections.visuals} timeout="auto" unmountOnExit>
                                        <Divider />
                                        <Box sx={{ px: 2.5, py: 2.5 }}>
                                            <TreatmentPlanVisualsNotes
                                                thumbnailUrl={thumbnailUrl}
                                                onThumbnailChange={setThumbnailUrl}
                                                patientNotes={patientNotes}
                                                onPatientNotesChange={setPatientNotes}
                                                options={options}
                                                onChange={setOptions}
                                            />
                                        </Box>
                                    </Collapse>
                                </Box>
                            </Box>
                        </m.div>
                    )}

                    {!showLinkScreen && wizardStep === 2 && (
                        <m.div
                            key="step-2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.3 }}
                        >
                            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                                <TreatmentPlanPreview
                                    planName={planName}
                                    options={options}
                                    planFields={planFields}
                                    thumbnailUrl={thumbnailUrl}
                                    patientNotes={patientNotes}
                                    patientName={patientName}
                                    phases={phases}
                                />
                            </Box>
                        </m.div>
                    )}
                </AnimatePresence>
            </DialogContent>

            {/* ── Footer (wizard steps 1-2 only) ── */}
            {!showLinkScreen && wizardStep > 0 && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
                    px: 3, py: 1.5,
                    bgcolor: '#fff', borderTop: '1px solid rgba(145,158,171,0.16)',
                    position: 'sticky', bottom: 0, zIndex: 10,
                }}>
                    {/* Left: Back */}
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={saving}
                        startIcon={<Iconify icon="eva:arrow-back-fill" width={16} />}
                        sx={{
                            textTransform: 'none', borderRadius: '10px', fontWeight: 600,
                            borderColor: 'rgba(145,158,171,0.3)', color: '#637381',
                        }}
                    >
                        Back
                    </Button>

                    {/* Right: Next / Generate */}
                    <Stack direction="row" spacing={1.5}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={saving}
                            sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, borderColor: 'rgba(145,158,171,0.3)', color: '#637381' }}
                        >
                            Cancel
                        </Button>

                        {wizardStep < TOTAL_STEPS - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={!canProceed}
                                endIcon={<Iconify icon="eva:arrow-forward-fill" width={16} />}
                                sx={{
                                    textTransform: 'none', borderRadius: '10px', fontWeight: 600,
                                    bgcolor: '#00AB55', '&:hover': { bgcolor: '#007B40' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(0,171,85,0.4)', color: '#fff' },
                                }}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleGenerate}
                                disabled={saving || !planName.trim()}
                                startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Iconify icon="eva:paper-plane-fill" width={16} />}
                                sx={{
                                    textTransform: 'none', borderRadius: '10px', fontWeight: 600,
                                    bgcolor: '#00AB55', '&:hover': { bgcolor: '#007B40' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(0,171,85,0.4)', color: '#fff' },
                                }}
                            >
                                {saving ? 'Generating...' : (isEditing ? 'Save & Generate Link' : 'Generate & Send')}
                            </Button>
                        )}
                    </Stack>
                </Box>
            )}
        </Dialog>
    );
}

TreatmentPlanCurationDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    leadDetail: PropTypes.object,
    existingPlan: PropTypes.object,
};

TreatmentPlanCurationDialog.defaultProps = {
    leadDetail: null,
    existingPlan: null,
};
